import { NEUCLEOS_SIGN_IN_ENDED_MESSAGE } from "@cleo/contracts";
import { Link } from "@tanstack/react-router";
import { ReauthDoor } from "./ReauthDoor";
import { LoaderIcon, RotateCcwIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import { allModeActive, worldDisplayName } from "../neucleos/worldSelection";
import { AllStanceOverview } from "./AllStanceOverview";
import { openWorldRoomConnections, worldViewBoardsSeated } from "../neucleos/worldViewController";
import { useWorldView, useWorldViewIfSeated, type WorldViewState } from "../neucleos/worldView";
import { useNeucleosWorldStore } from "../neucleos/worldStore";
import { worldViewFrameKind } from "../worldViewRoute";
import { NeucleosLocalDoor } from "./NeucleosWorldRail";
import { Button } from "./ui/button";
import { CleoWaitIndicator } from "./CleoWaitIndicator";
import { isWebHost } from "~/webHost";

/**
 * Typed absence for a governed world view. A room that is loading, refused,
 * or still redirecting is named as exactly that inside the intact shell —
 * never a blank body, and never a local execution wearing the World's name.
 * Every state keeps the two doors that always exist: Local and Connections.
 */

/** Whether the addressed leaf is a room the governed World must own. */
export function governedWorldViewBlocksLeaf(state: WorldViewState, pathname: string): boolean {
  if (state.kind === "local" || state.kind === "world-ready") return false;
  return pathname === "/" || worldViewFrameKind(pathname) === "session";
}

const REMEDY_BY_REFUSAL: Readonly<Record<string, string>> = {
  grant_stale:
    "This World's rooms aren't available to members in this version of Cleo. Signing in again won't change that.",
  tool_not_on_grant:
    "This World doesn't offer its rooms to this version of Cleo yet. They'll appear when the World is updated; you don't need to sign in again.",
  world_unresolved: "Open this World and sign in again from Connections.",
  transport: "The local Cleo server could not reach the World door. Retry, or check Connections.",
};

function RoomDoors({ worldName }: { readonly worldName: string }): ReactElement {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <Button
        render={
          <Link
            to="/settings/connections"
            onClick={(event) => {
              // A seated governed room hosts Connections as the Settings
              // Canvas tab; routed navigation is only the no-room fallback.
              if (worldViewBoardsSeated()) {
                event.preventDefault();
                openWorldRoomConnections();
              }
            }}
          />
        }
        size="sm"
        variant="outline"
      >
        Open Connections
      </Button>
      <NeucleosLocalDoor className="min-h-8 px-3 text-xs tracking-normal" selected={false} />
      <p className="basis-full text-xs leading-5 text-muted-foreground">
        Nothing shown here is governed by {worldName} until its rooms read back.
      </p>
    </div>
  );
}

export function WorldViewStateFace(): ReactElement | null {
  const { state, refresh } = useWorldView();
  const worlds = useNeucleosWorldStore((store) => store.worlds);
  const allStance = useNeucleosWorldStore((store) => allModeActive(store.selection));
  if (state.kind === "local") return null;
  // The ALL stance is not a missing World — it is the whole entitlement.
  // Its room is the entitlement page, never the refusal card.
  if (allStance && state.kind === "world-unavailable" && state.worldKey === null) {
    return <AllStanceOverview />;
  }
  const worldName = worldDisplayName(worlds, state.worldKey);

  if (state.kind === "world-loading") {
    return (
      <div
        className="grid min-h-0 flex-1 place-items-center p-8 text-sm text-muted-foreground"
        data-world-room-state="loading"
      >
        <CleoWaitIndicator label={`Opening ${worldName}…`} />
      </div>
    );
  }

  if (state.kind === "world-ready") {
    // The room provider is redirecting to the authoritative Session; name the
    // moment instead of rendering nothing while the URL catches up.
    const slice = state.index.sessions.find(
      (candidate) => candidate.sessionId === state.activeSessionId,
    );
    return (
      <div
        className="grid min-h-0 flex-1 place-items-center p-8 text-sm text-muted-foreground"
        data-world-room-state="opening"
      >
        <CleoWaitIndicator label={`Opening ${slice?.label ?? "session"}…`} />
      </div>
    );
  }

  // W-4a6095af: the World stopped accepting this Mac's sign-in, or custody of it has
  // ended (the next read says not_signed_in). The person reads one plain sentence and
  // the sign-in button; the refusal kind stays in data-world-room-refusal-kind only.
  const signInEnded =
    state.refusalKind === "grant_rejected" || state.refusalKind === "not_signed_in";
  const remedy =
    state.refusalKind === null || signInEnded
      ? null
      : (REMEDY_BY_REFUSAL[state.refusalKind] ?? null);
  return (
    <div className="grid min-h-0 flex-1 place-items-center overflow-y-auto p-6">
      <section
        aria-live="polite"
        className="w-full max-w-lg rounded-xl border border-destructive/40 bg-card p-5"
        data-world-room-refusal-kind={state.refusalKind ?? undefined}
        data-world-room-state="unavailable"
        role="status"
      >
        <div className="text-[10px] tracking-[0.2em] text-muted-foreground">GOVERNED ROOM</div>
        <h2 className="mt-2 text-lg font-semibold tracking-tight text-foreground">
          {state.worldKey === null ? "No World is entered" : `${worldName} rooms unavailable`}
        </h2>
        <p className="mt-2 text-sm leading-6 text-foreground">
          {signInEnded ? NEUCLEOS_SIGN_IN_ENDED_MESSAGE : state.reason}
        </p>
        {remedy === null ? null : (
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{remedy}</p>
        )}
        {state.retry ? (
          <p
            className="mt-2 flex items-center gap-2 text-sm leading-6 text-muted-foreground"
            data-world-room-retry={state.retry.attempt}
          >
            <LoaderIcon aria-hidden="true" className="size-3.5 animate-spin" />
            Reading again on its own: retry {state.retry.attempt} of {state.retry.of}.
          </p>
        ) : null}
        {/* EB-CAN-06: a re-auth-available refusal carries its one-click door. */}
        {state.refusalKind === null ? null : (
          <ReauthDoor className="mt-3" kind={state.refusalKind} onSettled={refresh} />
        )}
        {state.worldKey === null ? null : (
          <div className="mt-4">
            <Button onClick={refresh} size="sm">
              <RotateCcwIcon className="size-4" />
              Retry read
            </Button>
          </div>
        )}
        <RoomDoors worldName={worldName} />
      </section>
    </div>
  );
}

/**
 * Keeps every `_chat` leaf out of a governed World that has not read back.
 * Outside room paths (settings, pull requests, projects) the leaf renders
 * regardless, so Settings stays reachable in every state.
 */
export function GovernedWorldViewGate({
  pathname,
  children,
}: {
  readonly pathname: string;
  readonly children: ReactNode;
}): ReactElement {
  const { state } = useWorldView();
  // cleo-web (webHost.ts): the root leaf is the browser host's landing (the World conversations, read from the World
  // itself), never held behind the room projection. Every room leaf keeps the gate, and passes once the browser has read
  // the World's rooms through the window's own client (neucleos/webHostWorldDoors.ts), as the Desk does through its server.
  if (isWebHost() && pathname === "/") return <>{children}</>;
  if (governedWorldViewBlocksLeaf(state, pathname)) return <WorldViewStateFace />;
  return <>{children}</>;
}

/** Seated inside the World frame, so an acknowledged click stays visible
 * throughout its awaited door/readback and clears on success or refusal. */
export function WorldViewPendingIndicator(): ReactElement | null {
  const pendingLabel = useWorldViewIfSeated()?.pendingLabel ?? null;
  if (pendingLabel === null) return null;
  return (
    <div
      aria-busy="true"
      className="pointer-events-none absolute inset-0 z-40 grid place-items-center bg-background/40"
      data-world-operation-pending=""
    >
      <div className="rounded-2xl border border-border/60 bg-background/95 px-8 py-5 shadow-lg">
        <CleoWaitIndicator label={pendingLabel} />
      </div>
    </div>
  );
}
